import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import syncStyles from '../../shared/syncStyles';

/**
 * Token display helpers for the Foundations pages.
 *
 * Moved from the old single-file design system page, where they lived
 * alongside 22 section components.
 *
 * Token VALUES are read live from the cascade rather than hand-copied. The
 * old page hardcoded them as strings and they had already drifted —
 * `--space-ui-medium-l` was documented as 24px when theme.css says 28px.
 */

const readToken = (name) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/** Live value of a CSS custom property, re-read on resize and brand change. */
export const useTokenValue = (name) => {
  const [value, setValue] = useState(() => readToken(name));

  useEffect(() => {
    const update = () => setValue(readToken(name));
    update();

    window.addEventListener('resize', update);
    // Brand switching rewrites every token, so watch the attribute too.
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-brand'],
    });

    return () => {
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, [name]);

  return value;
};

/* ---------------------------------------------------------------------------
 * Responsive token pairs
 *
 * Spacing and type tokens flip at 1024px, so at any one window width the
 * cascade can only tell you half the story — the old page worked around that
 * by hardcoding both values, and they drifted. Two offscreen iframes, one
 * either side of the breakpoint, each resolve the same variable against their
 * own viewport. The table is then correct at any dashboard width, and still
 * read live.
 * ------------------------------------------------------------------------- */

const FRAME_WIDTHS = { mobile: 393, desktop: 1440 };

/**
 * `visibility: hidden` rather than `display: none` — a display-none frame has
 * no layout, so its media queries never resolve and every value comes back
 * as the mobile one.
 */
const createMeasureFrame = (width) => {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  frame.title = `Token measurement at ${width}px`;
  Object.assign(frame.style, {
    position: 'fixed',
    top: '0',
    left: '-10000px',
    width: `${width}px`,
    height: '200px',
    border: '0',
    visibility: 'hidden',
    pointerEvents: 'none',
  });
  document.body.appendChild(frame);
  return frame;
};

/**
 * Resolve several custom properties at both breakpoints at once.
 *
 * Returns `{ [name]: { mobile, desktop } }`. Values are `''` until the frames
 * are ready, and stay `''` under jsdom, which has no layout — callers should
 * fall back to an em dash.
 */
export const useResponsiveTokens = (names) => {
  const [values, setValues] = useState({});
  // Arrays are fresh on every render; the joined string is a stable dep.
  const key = names.join('|');

  useEffect(() => {
    const wanted = key.split('|').filter(Boolean);
    const frames = Object.entries(FRAME_WIDTHS).map(([band, width]) => [band, createMeasureFrame(width)]);

    const read = () => {
      const next = {};
      frames.forEach(([band, frame]) => {
        const doc = frame.contentDocument;
        if (!doc?.documentElement) return;
        syncStyles(doc);
        const computed = getComputedStyle(doc.documentElement);
        wanted.forEach((token) => {
          next[token] = { ...next[token], [band]: computed.getPropertyValue(token).trim() };
        });
      });
      setValues(next);
    };

    // A srcless same-origin frame usually has its document immediately, but
    // some browsers need a tick — mirror ViewportFrame and cover both.
    read();
    const raf = requestAnimationFrame(read);
    frames.forEach(([, frame]) => frame.addEventListener('load', read));

    // Brand switching rewrites every token; HMR injects new stylesheets.
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-brand'] });
    observer.observe(document.head, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      frames.forEach(([, frame]) => {
        frame.removeEventListener('load', read);
        frame.remove();
      });
    };
  }, [key]);

  return values;
};

/** A token name with its currently resolved value. */
export const TokenValue = ({ name }) => {
  const value = useTokenValue(name);
  return (
    <div className="p-[var(--space-ui-small-l)] bg-[var(--color-gray-0)] rounded-[var(--radius-inner-radius)] border border-[var(--color-gray-1)]">
      <code className="text-[var(--ds-doc-ink)] break-all">{name}</code>
      <div className="mt-[var(--space-ui-small-s)]">
        <span className="typo-body-sm text-[var(--color-gray-10)]">{value || '—'}</span>
      </div>
    </div>
  );
};

TokenValue.propTypes = {
  name: PropTypes.string.isRequired,
};

/**
 * Size and line height a `typo-*` class actually renders at, measured off the
 * rendered specimen.
 *
 * Not read from the token names: those don't track the class names — `.typo-body`
 * is `--typo-p-regular-font-size`, `.typo-label` is `--typo-p-label-*` — so a
 * hand-written class→token map is a drift waiting to happen. Measuring what the
 * browser produced cannot drift, and it picks up the 1024px switch and the brand
 * swap for free: resize the window and the numbers move.
 */
const useRenderedType = (ref) => {
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const read = () => {
      const styles = window.getComputedStyle(el);
      const size = parseFloat(styles.fontSize);
      // No stylesheet (jsdom) or nothing resolved yet — show nothing rather
      // than a wrong number.
      if (!size) {
        setMetrics(null);
        return;
      }
      const lineHeight = parseFloat(styles.lineHeight);
      setMetrics({ size, lineHeight: Number.isFinite(lineHeight) ? lineHeight : null });
    };

    read();
    window.addEventListener('resize', read);
    // Brand switching swaps the family and can swap the metrics with it.
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-brand'],
    });

    return () => {
      window.removeEventListener('resize', read);
      observer.disconnect();
    };
  }, [ref]);

  return metrics;
};

/** 13.2 stays 13.2, 48.0 becomes 48 — the scale has both. */
const px = (n) => String(Math.round(n * 10) / 10);

/**
 * One type specimen: what the style is called on the left, the style itself
 * set in it on the right.
 *
 * The specimen leads. Earlier this row baseline-aligned a three-line meta
 * block against a 48px heading inside 12px of padding, which read as one
 * compressed clump, and pinned the meta to a 192px column the longer class
 * names could not fit. Now the meta is a fixed rail that never wraps, the
 * padding is scaled for the biggest specimen on the page, and the two sides
 * are centred against each other instead of sharing a baseline.
 */
export const TypographyExample = ({ className, alias, label, children, fontType = 'ui' }) => {
  const specimenRef = useRef(null);
  const metrics = useRenderedType(specimenRef);

  return (
    <div className="ds-type">
      <div className="typo-body ds-type__meta">
        <span className="typo-body-sm ds-type__label">{label}</span>
        <code className="ds-type__alias">.{alias}</code>
        <span className="typo-body-sm ds-type__tags">
          <span className={`ds-type__badge ds-type__badge--${fontType}`}>
            {fontType === 'editorial' ? 'Editorial' : 'UI'}
          </span>
          {metrics && (
            <span className="ds-type__metrics">
              {px(metrics.size)}
              {metrics.lineHeight ? ` / ${px(metrics.lineHeight)}` : ''} px
            </span>
          )}
        </span>
      </div>
      <div className="ds-type__specimen">
        <span ref={specimenRef} className={className}>{children}</span>
      </div>
    </div>
  );
};

TypographyExample.propTypes = {
  className: PropTypes.string,
  alias: PropTypes.string,
  label: PropTypes.string,
  children: PropTypes.node,
  fontType: PropTypes.oneOf(['ui', 'editorial']),
};

/**
 * One colour token: the colour, what it is called, what it is called in code,
 * and what it currently resolves to. Live, so the page re-reads on a brand
 * switch.
 *
 * Two arrangements, same four facts — the layout bends to the content, never
 * the other way round:
 *
 *   `card` — a chip with the text beneath it, for the discrete named roles
 *            (brand, UI states, configurator).
 *   `row`  — a chip with the text laid out beside it, for the 17-step neutral
 *            ramp. A grid of 17 cards had to drop the variable name to stop
 *            it wrapping; a row has the width to carry every name in full AND
 *            stacks the chips into a strip you can read as a ramp.
 *
 * Dropped `textLight`: it muted the caption under dark swatches, which only
 * made those captions harder to read. The caption sits on the card, not on
 * the colour, so the colour has no say in it.
 */
export const ColorSwatch = ({ name, variable, layout = 'card' }) => {
  const value = useTokenValue(variable);
  const isRow = layout === 'row';

  const chip = <div className="ds-swatch__chip" style={{ backgroundColor: `var(${variable})` }} />;
  const text = (
    <>
      <span className="ds-swatch__name">{name}</span>
      <code className="ds-swatch__var">{variable}</code>
      {value && <code className="ds-swatch__value">{value}</code>}
    </>
  );

  if (isRow) {
    return (
      <div className="typo-body ds-swatch ds-swatch--row">
        {chip}
        <div className="ds-swatch__row-text">{text}</div>
      </div>
    );
  }

  return (
    <div className="typo-body ds-swatch">
      {chip}
      {text}
    </div>
  );
};

ColorSwatch.propTypes = {
  /** Human name — "Primary", "Gray 025" */
  name: PropTypes.string,
  /** The CSS custom property, shown verbatim */
  variable: PropTypes.string,
  /** `card` stacks the text under the chip; `row` sets it beside */
  layout: PropTypes.oneOf(['card', 'row']),
};

export const SpacingExample = ({ name, variable, highlight }) => {
  const value = useTokenValue(variable);
  return (
    <div className="flex items-center gap-[var(--space-ui-medium-m)]">
      <div className="w-32 shrink-0">
        <code className={`typo-body-sm ${highlight ? 'text-[var(--color-gray-10)]' : 'text-[var(--ds-doc-ink-muted)]'}`}>
          {name}
        </code>
      </div>
      <div
        className={`h-4 rounded-[var(--radius-button)] shrink-0 ${highlight ? 'bg-[var(--color-gray-10)]' : 'bg-[var(--color-gray-3)]'}`}
        style={{ width: `var(${variable})` }}
      />
      <span className="typo-body-sm text-[var(--color-gray-10)] w-12 shrink-0">{value}</span>
      <code className="text-[var(--ds-doc-ink-muted)]">{variable}</code>
    </div>
  );
};

SpacingExample.propTypes = {
  name: PropTypes.string,
  variable: PropTypes.string,
  highlight: PropTypes.bool,
};

export const RadiusExample = ({ name, variable, size = 'normal' }) => {
  const value = useTokenValue(variable);
  return (
    <div className="text-center">
      <div
        className={`${size === 'large' ? 'w-24 h-24' : 'w-16 h-16'} bg-[var(--color-gray-10)] mx-auto mb-[var(--space-ui-small-m)]`}
        style={{ borderRadius: `var(${variable})` }}
      />
      <p className="typo-body text-[var(--ds-doc-ink)]">{name} · {value}</p>
      <code className="text-[var(--ds-doc-ink-muted)]">{variable}</code>
    </div>
  );
};

RadiusExample.propTypes = {
  name: PropTypes.string,
  variable: PropTypes.string,
  size: PropTypes.oneOf(['normal', 'large']),
};
